// Capped, in-memory notification history — backs the footer bell tray.
//
// OPUS-1527. Drive used to surface operator feedback as fixed-position toast
// overlays. They sat on top of the controls and swallowed clicks on the buttons
// behind them, which mid-fixture is the difference between a graphic reaching
// air and not: the stack had no cap and no dedupe, so a single burst left a
// column of alerts covering the UI for five seconds each.
//
// Deliberately NOT modelled on the whole of commandLog.js. That file's header is
// a post-mortem of an unbounded, by-reference, localStorage-backed log that blew
// the ~5 MB origin quota inside one match and took sessionManager's session
// writes down with it. We want its in-memory half — module-authoritative array,
// pub/sub, hard caps — and none of its persistence. History is per-session by
// design; nothing here ever touches storage.
//
// The memory contract in one line: this module stores strings and numbers.
// Never a caller's object, never an Error (it pins .stack, a .cause chain and
// the scope that threw), never a React element (it pins a component tree).
// Everything is coerced at the boundary, in pushNotification.
//
// Worst case retained: MAX_ENTRIES x MAX_MESSAGE_CHARS ~= 35 KB. Typical ~5 KB.
//
// This module is framework-free on purpose — the React bindings live in
// hooks/useNotificationLog.js so the store can be unit-tested without a DOM.

export const MAX_ENTRIES = 50;
const MAX_MESSAGE_CHARS = 300;
// A repeat of the line we just said, inside this window, is the same incident
// rather than a new one. Ten seconds swallows a burst (a double-click, a
// command that fans into a BAKE+ANIMATE pair) without swallowing deliberate
// repetition — an operator who resets the clock twice in a match wants two rows.
//
// Collapsing is HEAD-ONLY. Scanning back over the newest N would mean either
// reordering history when an older line repeats, or silently bumping a counter
// at index 12 — which to the operator looks like nothing happened at all.
const DEDUPE_WINDOW_MS = 10_000;

const SEVERITIES = ['success', 'info', 'warning', 'error'];
const isSeverity = (value) => typeof value === 'string' && SEVERITIES.includes(value);

// Which severity the bell badge takes when several are unread. error beats
// warning beats the rest; success and info are both just "something happened".
const SEVERITY_RANK = { success: 0, info: 0, warning: 1, error: 2 };

// Per the AE design system, and matching the semantics already written down in
// utils/flagChangeMessage.js: green = confirmed, red = critical, amber = possible
// issue, purple = informational. Yellow #FCC144 doubles as the warning tone
// there; blue #4EC2F5 is absent on purpose because it means "you are here", and
// it is used for the unread badge instead of for any individual row.
export const SEVERITY_COLORS = {
  success: '#45FFA7',
  info: '#9B8BFF',
  warning: '#FCC144',
  error: '#FF455C',
};

let entries = []; // authoritative, newest first
let nextId = 1;
let listeners = new Set();

// useSyncExternalStore demands that getSnapshot return a referentially stable
// value between mutations, or React re-renders in a loop. Both snapshots are
// cached and only replaced when their contents actually move.
let entriesSnapshot = entries;
let summarySnapshot = { unread: 0, topSeverity: null };

/**
 * Reduce whatever a caller passed to a bounded, detached string.
 *
 * Errors are unwrapped to their .message rather than stored: holding the Error
 * itself would retain its stack, its cause chain, and the closure it was thrown
 * from — potentially a whole fixture's worth of objects, for one toast line.
 */
const coerceMessage = (value) => {
  let text;
  if (typeof value === 'string') text = value;
  else if (value == null) text = '';
  else if (typeof value.message === 'string') text = value.message;
  // Anything else (including a React element, which callers should not pass —
  // see @ae/lineup's buildSwapText) becomes a detached string, never a held ref.
  else text = String(value);

  text = text.trim();
  return text.length > MAX_MESSAGE_CHARS
    ? `${text.slice(0, MAX_MESSAGE_CHARS - 1)}…`
    : text;
};

const publish = () => {
  entriesSnapshot = entries;

  let unread = 0;
  let topSeverity = null;
  for (let i = 0; i < entries.length; i += 1) {
    const entry = entries[i];
    if (entry.read) continue;
    unread += 1;
    if (topSeverity === null || SEVERITY_RANK[entry.severity] > SEVERITY_RANK[topSeverity]) {
      topSeverity = entry.severity;
    }
  }

  // Replace the summary object ONLY when a value actually changed. The footer
  // bell subscribes to this one, and useSyncExternalStore bails out on Object.is
  // — so a push that moves neither the count nor the severity re-renders nothing.
  if (unread !== summarySnapshot.unread || topSeverity !== summarySnapshot.topSeverity) {
    summarySnapshot = { unread, topSeverity };
  }

  listeners.forEach((listener) => listener());
};

/**
 * Record a notification. Signature matches the old TXLive showNotification, so
 * every existing call site and every onNotify prop keeps working untouched.
 *
 * @param {string|Error} message
 * @param {'success'|'info'|'warning'|'error'} [severity]
 * @returns {number|null} the entry id, or null if there was nothing to say
 */
export function pushNotification(message, severity = 'error') {
  let body = message;
  let level = severity;

  // Defensive swap. LineupPanel called onNotify("success", "…") — i.e.
  // (severity, message) — while every other consumer used (message, severity),
  // so those rows rendered the literal word "success" as the body. The call
  // sites are fixed; this makes it unrepeatable.
  if (isSeverity(body) && !isSeverity(level)) {
    const held = body;
    body = level;
    level = held;
  }

  const text = coerceMessage(body);
  if (!text) return null;
  const lvl = isSeverity(level) ? level : 'error';
  const now = Date.now();

  const head = entries[0];
  if (head && head.message === text && head.severity === lvl && now - head.at < DEDUPE_WINDOW_MS) {
    // Collapse into the row we just wrote. It goes unread again — the repeat is
    // new information even when the wording is not.
    const collapsed = { ...head, count: head.count + 1, at: now, read: false };
    entries = [collapsed, ...entries.slice(1)];
    publish();
    return collapsed.id;
  }

  const entry = { id: nextId, message: text, severity: lvl, at: now, count: 1, read: false };
  nextId += 1;
  entries = [entry, ...entries];
  if (entries.length > MAX_ENTRIES) entries = entries.slice(0, MAX_ENTRIES);
  publish();
  return entry.id;
}

export function subscribeNotifications(listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export const getNotificationsSnapshot = () => entriesSnapshot;

export const getSummarySnapshot = () => summarySnapshot;

export function markAllRead() {
  if (summarySnapshot.unread === 0) return;
  entries = entries.map((entry) => (entry.read ? entry : { ...entry, read: true }));
  publish();
}

export function clearNotifications() {
  if (entries.length === 0) return;
  entries = [];
  publish();
}

/**
 * Test-only. Module state is a singleton, so suites have to be able to reset it
 * between cases.
 */
export function __resetNotificationLog() {
  entries = [];
  entriesSnapshot = entries;
  summarySnapshot = { unread: 0, topSeverity: null };
  nextId = 1;
  listeners = new Set();
}
